import { AlertController, LoadingController } from '@ionic/angular';
import { AnalyticsService } from '../analytics/analytics.service';
import { Api } from '../../api/api';
import { TranslocoService } from '@jsverse/transloco';
import { SignedOutError } from 'src/app/shared/errors/signed-out-error';
import { PAGE } from 'src/app/routes';
import { Router } from '@angular/router';

export class JobCancelledError extends Error {
  constructor(message = 'Task was cancelled') {
    super(message);
    this.name = 'TaskCancelledError';
  }

  static isInstance(error: unknown): boolean {
    return error instanceof JobCancelledError;
  }
}

export class JobErrorHandler<T> {
  private enableFatalError = false;
  private enableWindowReload = false;
  private static showingWidgetAbort = false;
  private onCancelHandler: (() => void) | null = null;
  private onErrorHandler: ((error: unknown) => Promise<void>) | null = null;

  constructor(
    private analyticsService: AnalyticsService,
    private alertController: AlertController,
    private translocoService: TranslocoService,
    private abortController: AbortController,
    private router: Router,
  ) {}

  async handle(job: () => Promise<T>): Promise<T> {
    try {
      return await job();
    } catch (error) {
      await this.analyticsService.handleError(error);
      if (!JobCancelledError.isInstance(error) && !this.abortController.signal.aborted) {
        if (SignedOutError.isInstance(error)) {
          if (!JobErrorHandler.showingWidgetAbort) {
            JobErrorHandler.showingWidgetAbort = true;
            await this.showWidgetAbortAlert();
          }
          throw error;
        }

        await this.onErrorHandler?.(error);
        this.enableFatalError && (await this.showFatalErrorAlert(error));
      }
      this.onCancelHandler?.();

      this.enableWindowReload && window.location.reload();

      throw error;
    }
  }

  showFatalError() {
    this.enableFatalError = true;
    return this;
  }

  reloadWindow() {
    this.enableWindowReload = true;
    return this;
  }

  onError(handler: (error: unknown) => Promise<void>) {
    this.onErrorHandler = handler;
    return this;
  }

  onCancel(handler: () => void) {
    this.onCancelHandler = handler;
    return this;
  }

  private async showFatalErrorAlert(error: unknown): Promise<void> {
    const context = error instanceof Error ? `: ${error.message}` : '';
    const alert = await this.alertController.create({
      cssClass: 'alert',
      header: this.translocoService.translate('job.unexpected-error'),
      message: `${this.translocoService.translate('job.unexpected-error-message')}${context}`,
      buttons: [{ cssClass: 'alert-button', text: this.translocoService.translate('job.ok') }],
      backdropDismiss: false,
    });
    await alert.present();
    await alert.onDidDismiss();
  }

  private async showWidgetAbortAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translocoService.translate('service.alert.fatal-error-header'),
      message: this.translocoService.translate('service.alert.fatal-error-message'),
      cssClass: 'alert',
      buttons: [
        {
          text: this.translocoService.translate('service.alert.ok-button'),
          cssClass: 'alert-button',
          handler: async () => {
            await this.router.navigate([PAGE.LOADING]);
            JobErrorHandler.showingWidgetAbort = false;
          },
        },
      ],
      backdropDismiss: false,
    });
    await alert.present();
    await alert.onDidDismiss();
  }
}

export class Job<T> {
  private attempt = 0;
  private canRetry?: (attempt: number) => boolean;
  private enableLoading = false;
  private loadingDelay = 1000;
  private abortController = new AbortController();
  private jobErrorHandler?: JobErrorHandler<T>;

  static readonly InfiniteRetry = (_attempt: number) => true;
  static readonly DefaultRetry = (attempt: number) => attempt < 3;

  constructor(
    private task: () => Promise<T>,
    private analyticsService: AnalyticsService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private translocoService: TranslocoService,
    private router: Router,
  ) {}

  async run(): Promise<T> {
    if (this.jobErrorHandler) {
      return await this.jobErrorHandler.handle(() => this.execute());
    }
    return await this.execute();
  }

  async runNoThrow(): Promise<T | undefined> {
    try {
      return await this.run();
    } catch (error) {
      return undefined;
    }
  }

  addErrorHandler() {
    this.jobErrorHandler = new JobErrorHandler<T>(
      this.analyticsService,
      this.alertController,
      this.translocoService,
      this.abortController,
      this.router,
    );
    return this.jobErrorHandler;
  }

  errorHandler() {
    return this.jobErrorHandler;
  }

  showNoInternetRetry(canRetry: (attempt: number) => boolean = Job.DefaultRetry) {
    this.canRetry = canRetry;
    return this;
  }

  showSpinner(delay = 1000) {
    this.enableLoading = true;
    this.loadingDelay = delay;
    return this;
  }

  cancel() {
    this.abortController.abort();
  }

  private checkIfCancelled() {
    if (this.abortController.signal.aborted) {
      throw new JobCancelledError('Job was cancelled');
    }
  }

  async execute(): Promise<T> {
    if (this.canRetry) {
      return this.handleRetry(async () => {
        return this.enableLoading ? this.handleLoading(this.task) : this.task();
      });
    } else {
      return this.enableLoading ? this.handleLoading(this.task) : this.task();
    }
  }

  private async handleLoading<T>(task: () => Promise<T>): Promise<T> {
    this.checkIfCancelled();
    let loading: HTMLIonLoadingElement | undefined;
    let timeout: NodeJS.Timeout | undefined;
    try {
      loading = await this.loadingController.create({ cssClass: 'custom-loading' });
      timeout = setTimeout(() => {
        if (!this.abortController.signal.aborted) loading?.present();
      }, this.loadingDelay);

      return await task();
    } catch (error) {
      this.checkIfCancelled();
      this.analyticsService.handleError(error);
      throw error;
    } finally {
      clearTimeout(timeout);
      if (loading && loading.isConnected) {
        await loading.dismiss();
      }
    }
  }

  private async handleRetry<T>(job: () => Promise<T>): Promise<T> {
    this.checkIfCancelled();
    try {
      return await job();
    } catch (error) {
      this.checkIfCancelled();
      if (!Api.isNoInternetError(error)) {
        await this.analyticsService.handleError(error);
        throw error;
      }

      const retryAllowed = this.canRetry && this.canRetry(this.attempt);
      this.attempt++;
      this.loadingDelay = 0;

      return new Promise<T>((resolve, reject) => {
        const cancelledError = new JobCancelledError('User cancelled');
        this.alertController
          .create({
            header: this.translocoService.translate('job.no-internet'),
            message: retryAllowed
              ? this.translocoService.translate('job.no-internet-message-retry')
              : this.translocoService.translate('job.no-internet-message'),
            buttons: retryAllowed
              ? [
                  {
                    text: this.translocoService.translate('job.cancel'),
                    handler: () => reject(cancelledError),
                  },
                  {
                    text: this.translocoService.translate('job.retry'),
                    handler: () => {
                      this.alertController.dismiss().then(() => {
                        if (!this.abortController.signal.aborted) {
                          this.handleRetry(job).then(resolve).catch(reject);
                        } else {
                          reject(cancelledError);
                        }
                      });
                    },
                  },
                ]
              : [
                  {
                    text: this.translocoService.translate('job.ok'),
                    handler: () => reject(cancelledError),
                  },
                ],
            backdropDismiss: false,
          })
          .then((alert) => alert.present());
      });
    }
  }
}
